import { type JSX } from 'react';
import { enableFlagJsonDiffing } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { capitalize } from '@gonfalon/strings/capitalize';

import { useApprovalRequestContext } from '#components/Approvals/utils/approvalRequestContext';
import { ApprovalProgressStatus } from '#utils/approvals/constants';
import type { VariationType } from '#utils/flags/flagConstants';

import { VARIATION_NOT_FOUND } from './instructionListUtils';
import { UpdateVariationJsonDiff } from './UpdateVariationJsonDiff';
import { VariationValue } from './variationInstructionListItemUtils';

import styles from './styles.module.css';

export type UpdateVariationInstructionEntryProps = {
  variationType: VariationType;
  currentVariationName: string | undefined;
  isVariationNotFound?: boolean;
  name?: { current?: string; updated?: string };
  description?: { current?: string; updated?: string };
  value?: { current: string; updated?: string };
};

export function UpdateVariationInstructionEntry({
  isVariationNotFound,
  variationType,
  currentVariationName,
  name: providedName,
  description: providedDescription,
  value: providedValue,
}: UpdateVariationInstructionEntryProps) {
  const { approvalRequest } = useApprovalRequestContext();
  const status = approvalRequest?.getProgressStatus();
  const name = providedName && { ...providedName };
  const description = providedDescription && { ...providedDescription };
  const value = providedValue && { ...providedValue };
  const isJsonDiffingEnabled = enableFlagJsonDiffing();

  const notFoundContent = capitalize(VARIATION_NOT_FOUND);

  if (isVariationNotFound) {
    if (name) {
      name.current = notFoundContent;
    }
    if (description) {
      description.current = notFoundContent;
    }
  }
  const showingJsonDiff = isJsonDiffingEnabled && variationType === 'json' && !isVariationNotFound;

  return (
    <div className={styles.VariationInstructionEntry}>
      <ul className={styles.ChangeList}>
        <MetadataChangeEntry label="name" value={name} />
        <MetadataChangeEntry label="description" value={description} />
        {value?.updated !== undefined && (
          <li>
            <span>
              Change value
              {currentVariationName && !isVariationNotFound && (
                <>
                  {' '}
                  of variation <span className={styles.MetadataValue}>{currentVariationName}</span>
                </>
              )}
            </span>

            {showingJsonDiff ? (
              <ErrorBoundary severity="high" fallbackRender={() => <div>Error rendering JSON diff</div>}>
                <UpdateVariationJsonDiff current={value.current} updated={value.updated} />
              </ErrorBoundary>
            ) : (
              <ChangeDiff
                from={
                  // As we pull the 'from' value from the current state, we don't want to show it if the approval request has been applied
                  status === ApprovalProgressStatus.COMPLETED ? undefined : isVariationNotFound ? (
                    <span>{notFoundContent}</span>
                  ) : (
                    <VariationValue value={value.current} variationType={variationType} />
                  )
                }
                to={<VariationValue value={value.updated} variationType={variationType} />}
              />
            )}
          </li>
        )}
      </ul>
    </div>
  );
}

function ChangeDiff({ from, to }: { from?: JSX.Element; to: JSX.Element }) {
  return (
    <div className={styles.ChangeDiff}>
      {from && (
        <>
          <span className={styles.changeEntryLabel}>from:</span> <span>{from}</span>
        </>
      )}
      <>
        <span className={styles.changeEntryLabel}>to:</span> <span>{to}</span>
      </>
    </div>
  );
}

function MetadataChangeEntry({
  label,
  value,
}: {
  label: 'name' | 'description';
  value: UpdateVariationInstructionEntryProps['name'];
}) {
  if (value?.current && value.updated === '') {
    // updating to the empty string means we are removing the property's current value
    return (
      <li className={styles.ChangeEntry}>
        Remove {label} <span className={styles.MetadataValue}>{value.current}</span>
      </li>
    );
  }

  if (value?.current && value.updated) {
    return (
      <li className={styles.ChangeEntry}>
        <span>Change {label}</span>
        <ChangeDiff
          from={<span className={styles.MetadataValue}>{value.current}</span>}
          to={<span className={styles.MetadataValue}>{value.updated}</span>}
        />
      </li>
    );
  }

  if (value?.updated) {
    return (
      <li className={styles.ChangeEntry}>
        Set {label} to <span className={styles.MetadataValue}>{value.updated}</span>
      </li>
    );
  }

  return null;
}
